import {
  confirmationBadge,
  confirmationStateOf,
  isLiveBooking,
  lifecycleLabel,
  type ConfirmationBadge,
  type LifecycleLabel,
} from '@atink/domain';
import type { Booking, Deposit } from '@/domain/types';
import type { DepositLevel } from '@/utils/statusTone';
import { dayKey } from '@/utils/studioDay';

/**
 * What a booking tile SAYS, derived once from the W4 booking-states contract so
 * the calendar and the Floor never disagree (G9):
 *   - lifecycle label ("Booked" / "Held" / "Checked in" / "In chair" / "Done" …);
 *   - the confirmation badge, only while the call still matters (pre-arrival).
 *     On the booking's own day it is the Floor's day-of badge
 *     (`floorConfirmationBadge`: "Not confirmed" until the client said yes), so a
 *     booking moved into today reads the same on the calendar and the Floor;
 *   - the deposit chip, never on a dead booking;
 *   - the ghost style: HELD (unpaid hold, DECIDED 24 Sep) or DEAD (cancelled /
 *     no-show, shown behind "Show cancelled");
 *   - whether it blocks the slot (dead ones never do — G8).
 */
export interface BookingTileState {
  lifecycle: LifecycleLabel;
  badge: ConfirmationBadge | null;
  depositChip: 'Deposit paid' | 'Part-paid' | 'Moving · partial due' | null;
  /**
   * HELD (unpaid hold), MOVED (a top-up move's held new date, calendar-12: the
   * money is on the old booking until the partial lands) or DEAD.
   */
  ghost: 'held' | 'moved' | 'dead' | null;
  /** The ghost's tile classes (a part-paid hold keeps its shade readable). */
  ghostClass: string | null;
  blocksSlot: boolean;
}

const PRE_ARRIVAL = new Set(['held', 'pending-deposit', 'confirmed', 'rescheduled']);
const HELD = new Set(['held', 'pending-deposit']);

export function bookingTileState(b: Booking, depositLevel: DepositLevel, nowIso: string): BookingTileState {
  const live = isLiveBooking(b);
  const state = confirmationStateOf(b, nowIso);
  const preArrival = live && PRE_ARRIVAL.has(b.status);
  const today = dayKey(b.start) === dayKey(nowIso);
  const ghost: BookingTileState['ghost'] = !live
    ? 'dead'
    : HELD.has(b.status)
      ? b.rescheduledFromBookingId
        ? 'moved'
        : 'held'
      : null;
  return {
    lifecycle: lifecycleLabel(b),
    badge: !preArrival
      ? null
      : today
        ? floorConfirmationBadge(b, nowIso)
        : state !== 'not-in-cadence'
          ? confirmationBadge(state)
          : null,
    depositChip: !live
      ? null
      : ghost === 'moved'
        ? 'Moving · partial due'
        : depositLevel === 'full'
          ? 'Deposit paid'
          : depositLevel === 'partial'
            ? 'Part-paid'
            : null,
    ghost,
    ghostClass: ghost === 'held' && depositLevel === 'partial' ? HELD_PART_PAID_CLASS : ghost ? GHOST_TILE_CLASS[ghost] : null,
    blocksSlot: live,
  };
}

/**
 * The Floor's day-of confirmation badge (W4 DECIDED 4 — the day-strip
 * Confirmations screen is retired, so today's unconfirmed arrivals show here):
 * "Confirmed" / "Confirmed at booking" once the client said yes, otherwise
 * "Not confirmed" (including a booking moved today — it is back in the cadence).
 */
export function floorConfirmationBadge(b: Booking, nowIso: string): ConfirmationBadge {
  const state = confirmationStateOf(b, nowIso);
  if (state === 'confirmed' || state === 'confirmed-at-booking') return confirmationBadge(state);
  return { label: 'Not confirmed', tone: 'blood' };
}

/** Tile classes for a ghost: translucent + dashed. Dead ghosts let clicks through to the free slot. */
export const GHOST_TILE_CLASS: Record<'held' | 'moved' | 'dead', string> = {
  held: 'opacity-60 !border-dashed !shadow-none',
  // calendar-12 (a): a moved booking's new date — full strength, dotted double edge.
  moved: '!border-2 !border-dotted !shadow-none',
  dead: 'opacity-35 !border-dashed !shadow-none pointer-events-none',
};

/** calendar-12 (c): a part-paid hold stays dashed but isn't faded, so its shade reads. */
const HELD_PART_PAID_CLASS = 'opacity-90 !border-dashed !shadow-none';

/** Pill classes per confirmation-badge tone (calendar tiles + Floor rows). */
export const BADGE_TONE_CLASS: Record<ConfirmationBadge['tone'], string> = {
  cream: 'border border-cream-faint/50 text-cream-soft',
  gold: 'border border-gold-bright/60 bg-gold/15 text-gold-bright',
  blood: 'border border-blood/60 bg-blood/10 text-blood',
  mint: 'border border-mint/60 bg-mint/10 text-mint',
};

/**
 * The deposit a tile is shaded by: its own, else (calendar-12 a) for a top-up
 * move's held new date, which carries no depositId until its partial lands so a
 * cancel of it never touches the plan's money, the deposit on the booking it moves.
 */
export function tileDeposit(
  b: Pick<Booking, 'depositId' | 'rescheduledFromBookingId'>,
  deposits: Record<string, Deposit | undefined>,
): Deposit | undefined {
  if (b.depositId) return deposits[b.depositId];
  const from = b.rescheduledFromBookingId;
  return from ? Object.values(deposits).find((d) => d?.bookingId === from) : undefined;
}
